// React i hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Tipovi i konstante
import { LeasingDetailFormValues } from "services/api/calculations/leasing/types";
import { Errors } from "services/forms/validation/types";
import { LeasingDetailPageCmp, LeasingDetailPageProps } from "./types";
import { CALCULATION_LEASING_DETAIL_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { SIDEBAR_ITEM_CALCULATION_LEASING } from "app/components/Sidebar/consts";

// API servisi
import { getLeasingAPI } from "services/api/calculations/leasing/api";

// UI komponente
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { Modal } from "ui/components/Modal";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { Button } from "ui/components/Button/Button";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";

// Lokalne komponente
import LeasingDetailCard from "./components/LeasingDetailCard/LeasingDetailCard";
import LeasingDeleteCard from "./components/LeasingDeleteCard/LeasingDeleteCard";

// Helperi
import { transformRoute, useTranslate } from "helpers";

// Navigacija
import { Routes } from "ui/modules/calculation/navigation";

// Konstante
import { initialLeasingDetailForm } from "services/api/calculations/leasing/consts";

const LeasingDetailPage: LeasingDetailPageCmp = (
  props: LeasingDetailPageProps
) => {
  const { leasingId } = useParams();

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<LeasingDetailFormValues>(
    initialLeasingDetailForm
  );

  const navigate = useNavigate();

  const title = CALCULATION_LEASING_DETAIL_PAGE_TITLE;

  useEffect(() => {
    if (leasingId) fetchLeasing(leasingId);
  }, [leasingId]);

  const fetchLeasing = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialLeasingDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getLeasingAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: SIDEBAR_ITEM_CALCULATION_LEASING.text,
      route: SIDEBAR_ITEM_CALCULATION_LEASING.route,
    },
  ];

  const handleDeleteSuccess = useCallback(() => {
    navigate(
      transformRoute(Routes.CalculationLeasingSearchPageRouter, {
        leasingId,
      })
    );
  }, []);

  if (!leasingId) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white border-b border-slate-200 rounded-t-md">
          <PageHeader>
            <PageTitle
              title={`${t(title.text)} [${formValues.name}]`}
              icon={title.icon}
              iconColor={title.iconColor}
            />
            <div className="flex justify-between">
              <Breadcrumb items={breadcrumbs} />
              <div className="flex gap-2">
                <Button
                  size="large"
                  variant="remove"
                  children="Obriši leasing"
                  onClick={() => setShowDeleteModal(true)}
                  startIcon="/media/icons/trash.svg"
                />
              </div>
            </div>
          </PageHeader>
        </div>
        <div className="px-4 py-6  lg:px-4">
          <LeasingDetailCard leasingId={leasingId} />
        </div>
      </div>
      {showDeleteModal && (
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <ModalHeader
            title={t("admin.calculation.setting.leasing.delete.title")}
            onHide={() => setShowDeleteModal(false)}
          />
          <ModalBody>
            <LeasingDeleteCard
              leasingId={leasingId}
              onSuccess={handleDeleteSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

LeasingDetailPage.route = Routes.CalculationLeasingDetailPageRouter;

export default LeasingDetailPage;
